import React from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight, Heart, LogOut, Search } from 'lucide-react';
import { getSavedEmail, listNotifications } from '../lib/watchlist';
import { useAuth } from '../lib/auth';

const links = [
  { to: '/marketplace', label: 'Marketplace' },
  { to: '/rides',       label: 'Rides' },
  { to: '/events',      label: 'Events' },
  { to: '/maps',        label: 'Maps' },
  { to: '/pricing',     label: 'Pricing' },
  { to: '/download',    label: 'Download' },
];

export default function Navbar({ onOpenCommand }) {
  const [open, setOpen] = React.useState(false);
  const [menu, setMenu] = React.useState(false);
  const [unread, setUnread] = React.useState(0);
  const [scrolled, setScrolled] = React.useState(false);
  const [isMac, setIsMac] = React.useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const menuRef = React.useRef(null);
  const menuBtnRef = React.useRef(null);

  React.useEffect(() => {
    if (typeof navigator !== 'undefined') {
      setIsMac(/Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent));
    }
  }, []);

  // Close everything on route change
  React.useEffect(() => { setOpen(false); setMenu(false); }, [location.pathname]);

  // Scroll-condense
  React.useEffect(() => {
    const onScroll = () => setScrolled((window.scrollY || 0) > 6);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close user dropdown on outside click or Escape
  React.useEffect(() => {
    if (!menu) return;
    const onClick = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (menuBtnRef.current?.contains(e.target)) return;
      setMenu(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenu(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  // Lock body scroll while the mobile drawer is open
  React.useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Poll unread notifications count when a saved email exists
  React.useEffect(() => {
    const email = getSavedEmail();
    if (!email) { setUnread(0); return; }
    let cancelled = false;
    const tick = async () => {
      try {
        const { unread_count } = await listNotifications(email);
        if (!cancelled) setUnread(unread_count || 0);
      } catch { /* silent */ }
    };
    tick();
    const iv = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [location.pathname]);

  return (
    <header
      className="am-glass am-nav sticky top-0 z-50"
      data-testid="site-navbar"
      data-scrolled={scrolled ? 'true' : 'false'}
    >
      <div className={`mx-auto max-w-7xl px-6 lg:px-10 flex items-center justify-between transition-[height] duration-300 ${scrolled ? 'h-14' : 'h-16'}`}>
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <div className="w-8 h-8 rounded-sm bg-[#0d0d0d] border border-[#262626] grid place-items-center transition-transform duration-300 group-hover:rotate-[-6deg]">
            <div className="w-4 h-2 bg-[#ff2a2a]" style={{ clipPath: 'polygon(0 100%, 20% 0, 80% 0, 100% 100%)' }} />
          </div>
          <div className="font-display font-bold tracking-tight text-lg uppercase">
            Auto<span className="text-[#ff2a2a]">Mobile</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Main">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `am-nav-link px-3.5 py-2 text-sm font-medium tracking-wide ${isActive ? 'text-white' : 'text-zinc-400 hover:text-white'}`
              }
              data-active={location.pathname.startsWith(l.to)}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {onOpenCommand && (
            <button
              onClick={onOpenCommand}
              className="hairline hover:border-[#ff2a2a] text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-2 px-3 py-1.5 text-xs"
              data-testid="nav-cmdk-trigger"
              aria-label="Open command palette"
              title="Search anything (⌘K)"
            >
              <Search className="w-3.5 h-3.5" />
              <span className="text-zinc-500">Search</span>
              <span className="hidden xl:inline-flex items-center gap-0.5 ml-2">
                <kbd className="hairline px-1 py-0.5 text-[10px] leading-none text-zinc-400 bg-[#0a0a0a]">{isMac ? '⌘' : 'Ctrl'}</kbd>
                <kbd className="hairline px-1 py-0.5 text-[10px] leading-none text-zinc-400 bg-[#0a0a0a]">K</kbd>
              </span>
            </button>
          )}
          <Link
            to="/watchlist"
            data-testid="nav-watchlist"
            className="relative p-2 text-zinc-300 hover:text-[#ff2a2a] transition-colors"
            title="Saved listings"
            aria-label={unread > 0 ? `Saved listings, ${unread} unread` : 'Saved listings'}
          >
            <Heart className={`w-4 h-4 transition-transform ${unread > 0 ? 'text-[#ff2a2a] fill-current scale-110' : ''}`} />
            {unread > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 grid place-items-center bg-[#ff2a2a] text-white text-[9px] font-mono rounded-full am-dot"
                data-testid="watchlist-badge"
              >
                {unread}
              </span>
            )}
          </Link>
          <Link
            to="/signin"
            data-testid="nav-signin"
            className={`text-sm text-zinc-300 hover:text-white transition-colors px-3 py-2 ${user ? 'hidden' : ''}`}
          >
            Sign in
          </Link>
          {user && (
            <div className="relative" data-testid="nav-user-menu">
              <button
                ref={menuBtnRef}
                onClick={() => setMenu((m) => !m)}
                className="flex items-center gap-2 pl-1 pr-2 py-1 hairline hover:border-[#ff2a2a] transition-colors"
                data-testid="nav-user-avatar"
                aria-haspopup="menu"
                aria-expanded={menu}
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-[#ff2a2a] text-white grid place-items-center text-xs font-semibold">
                    {(user.name || user.email || '?')[0].toUpperCase()}
                  </div>
                )}
                <span className="text-xs text-white max-w-[100px] truncate">{user.name || user.email}</span>
              </button>
              {menu && (
                <div
                  ref={menuRef}
                  className="am-dropdown-in absolute right-0 top-full mt-2 w-56 bg-[#0d0d0d] hairline shadow-2xl z-50"
                  data-testid="nav-user-dropdown"
                  role="menu"
                >
                  <div className="p-3 hairline-b">
                    <div className="text-xs text-zinc-500 font-mono uppercase tracking-widest">Signed in as</div>
                    <div className="text-sm text-white truncate">{user.email}</div>
                  </div>
                  {[
                    { to: '/watchlist', label: 'Saved listings',        testid: 'menu-watchlist' },
                    { to: '/profile',   label: 'My profile & sales',    testid: 'menu-profile' },
                    { to: '/billing',   label: 'Billing & invoices',    testid: 'menu-billing' },
                    { to: '/sell',      label: 'Sell a vehicle',        testid: 'menu-sell' },
                  ].map((m) => (
                    <Link
                      key={m.to}
                      to={m.to}
                      role="menuitem"
                      data-testid={m.testid}
                      className="block px-3 py-2.5 text-sm text-zinc-200 hover:bg-white/5 hover:text-[#ff2a2a] transition-colors"
                    >
                      {m.label}
                    </Link>
                  ))}
                  <button
                    onClick={logout}
                    role="menuitem"
                    className="w-full text-left px-3 py-2.5 text-sm text-zinc-200 hover:bg-white/5 hover:text-[#ff2a2a] transition-colors inline-flex items-center gap-2"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-3.5 h-3.5" /> Sign out
                  </button>
                </div>
              )}
            </div>
          )}
          <Link
            to="/download"
            data-testid="nav-get-app"
            className="group inline-flex items-center gap-1.5 bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold px-4 py-2 rounded-sm transition-colors"
          >
            Get the app <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-1">
          {onOpenCommand && (
            <button
              onClick={onOpenCommand}
              className="text-white p-2 hover:bg-white/5 rounded-sm transition-colors"
              data-testid="nav-mobile-cmdk"
              aria-label="Search"
            >
              <Search className="w-4 h-4" />
            </button>
          )}
          <button
            className="text-white p-2 hover:bg-white/5 rounded-sm transition-colors"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            <span className="relative block w-5 h-5">
              <Menu className={`w-5 h-5 absolute inset-0 transition-all duration-200 ${open ? 'opacity-0 rotate-90 scale-75' : 'opacity-100 rotate-0 scale-100'}`} />
              <X    className={`w-5 h-5 absolute inset-0 transition-all duration-200 ${open ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-75'}`} />
            </span>
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden hairline-t bg-black am-drawer-in" data-testid="mobile-nav-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 text-sm font-medium hairline-b flex items-center justify-between ${isActive ? 'text-white' : 'text-zinc-300'}`
                }
              >
                <span>{l.label}</span>
                <ChevronRight className="w-3.5 h-3.5 text-zinc-600" />
              </NavLink>
            ))}
            <Link to="/watchlist" className="px-3 py-3 text-sm text-zinc-300 hairline-b inline-flex items-center gap-2" data-testid="nav-mobile-watchlist">
              <Heart className="w-3.5 h-3.5" /> Saved
              {unread > 0 && <span className="ml-auto bg-[#ff2a2a] text-white text-[9px] font-mono px-1.5 py-0.5 rounded-full">{unread}</span>}
            </Link>
            {user ? (
              <>
                <Link to="/profile" className="px-3 py-3 text-sm text-zinc-300 hairline-b" data-testid="nav-mobile-profile">My account</Link>
                <button onClick={logout} className="px-3 py-3 text-sm text-zinc-300 hairline-b text-left inline-flex items-center gap-2" data-testid="nav-mobile-signout">
                  <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
              </>
            ) : (
              <Link to="/signin" className="px-3 py-3 text-sm text-zinc-300 hairline-b" data-testid="nav-mobile-signin">Sign in</Link>
            )}
            <Link to="/download" className="mt-3 bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-center py-2.5 rounded-sm font-semibold transition-colors" data-testid="nav-mobile-get-app">
              Get the app
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
